/**
 * Toolbar — Loop, PiP, Cast, CopyURL, Settings, Help.
 *
 * Renders into a host element provided by the caller (header-slot in modal
 * mode, or a position-absolute overlay in inline mode). All buttons wire
 * directly to the ZapPlayer public API; cast/copy-URL go through the existing
 * attachCastButtons helper from /opt/zap/frontend/src/core/cast.js.
 *
 * The stats-button was retired together with ui/stats.js — engine
 * telemetry now lives in the consolidated debug-overlay (D-key toggle,
 * see zapd-engine-bridge.js mountDebugOverlay).
 *
 * Returns an object with imperative state setters so the player can sync
 * the toolbar when state changes elsewhere (e.g. Settings-Panel toggles loop).
 */
import { icon } from '../../core/icons.js';
import { attachCastButtons } from '../../core/cast.js';

/**
 * @param {Object} player    ZapPlayer instance (uses .video, .setLoop, .togglePip)
 * @param {HTMLElement} hostEl
 * @param {Object} opts
 * @param {Object} [opts.features]
 * @param {string} [opts.castMediaUrl]   token-signed URL for cast / clipboard
 * @param {string} [opts.name]
 * @param {string} [opts.poster]
 * @param {() => number} [opts.getCurrentTime]
 * @param {() => void} [opts.onToggleSettings]
 * @param {boolean} [opts.initialLoop]
 */
export function createToolbar(player, hostEl, opts = {}) {
  const {
    features = {},
    castMediaUrl = '',
    name = '',
    poster = null,
    getCurrentTime = null,
    onToggleSettings = () => {},
    initialLoop = false,
  } = opts;

  hostEl.innerHTML = [
    `<button class="po-btn" data-tb="loop" title="Loop (Endlos-Wiedergabe)" aria-pressed="${initialLoop}">↻</button>`,
    features.pip !== false
      ? `<button class="po-btn" data-tb="pip" title="Picture-in-Picture (P)">⧉</button>`
      : '',
    features.cast !== false
      ? `<button class="po-btn" data-cast-launcher title="Cast / Chromecast" hidden>📺</button>`
      : '',
    `<button class="po-btn" data-cast-copyurl title="Stream-URL für mpv/VLC kopieren">${icon('externalLink', { size: 14 })}</button>`,
    features.settings !== false
      ? `<button class="po-btn po-btn--gear" data-tb="settings" title="Einstellungen">⚙</button>`
      : '',
    features.help !== false
      ? `<button class="po-btn" data-tb="help" title="Tastatur-Hilfe (?)">?</button>`
      : '',
  ].filter(Boolean).join('');

  const loopBtn  = hostEl.querySelector('[data-tb="loop"]');
  const pipBtn   = hostEl.querySelector('[data-tb="pip"]');
  const settBtn  = hostEl.querySelector('[data-tb="settings"]');
  const helpBtn  = hostEl.querySelector('[data-tb="help"]');

  if (initialLoop) loopBtn?.classList.add('is-on');

  loopBtn?.addEventListener('click', () => {
    const on = !player.video.loop;
    player.setLoop(on);
    api.setLoopState(on);
  });
  pipBtn?.addEventListener('click', () => player.togglePip());
  settBtn?.addEventListener('click', () => onToggleSettings());
  helpBtn?.addEventListener('click', () => player.openHelp?.());

  // Wire cast launcher + copy-URL via the existing helper. It looks for
  // [data-cast-launcher] / [data-cast-copyurl] anywhere in the passed-in
  // container, so we walk up to the overlay if we're nested.
  const overlayForCast = hostEl.closest('.player-overlay') || hostEl;
  attachCastButtons(overlayForCast, {
    mediaUrl: castMediaUrl,
    contentType: 'application/x-mpegURL',
    title: name,
    poster,
    getCurrentTime,
  });

  const api = {
    hostEl,
    setLoopState(on) {
      loopBtn?.classList.toggle('is-on', on);
      loopBtn?.setAttribute('aria-pressed', String(on));
    },
    setSettingsState(on) {
      settBtn?.classList.toggle('is-on', !!on);
    },
  };
  return api;
}
